import type { ImeiHubServiceId } from '../components/ImeiHubSignalIcon'
import { formatVerifyDeviceTitle } from './formatVerifyDeviceTitle'

export type ImeiReportStatus = 'clean' | 'warning' | 'high_risk' | 'invalid' | 'balance'

export type SectionChipTone = 'ok' | 'warn' | 'risk' | 'unknown' | 'muted'

export type ImeiCheckPayload = {
  is_valid?: boolean
  is_stolen?: boolean | null
  is_blacklisted?: boolean | null
  is_financed?: boolean | null
  icloud_locked?: boolean | null
  imei_eu_balance_required?: boolean
  brand?: string | null
  model?: string | null
  carrier?: string | null
  message?: string | null
  error?: string | null
  device?: {
    color?: string | null
    release_year?: number | null
    tac?: string | null
    provider?: string | null
  } | null
  risk_warnings?: string[] | null
  risk_notes?: string[] | null
}

export type ReportSection = {
  id: string
  tone: SectionChipTone
  detail?: string
  /** Hub preview rows — title from i18n key instead of section id map. */
  titleKey?: string
}

export type ImeiReportViewModel = {
  status: ImeiReportStatus
  trustScore: number | null
  deviceLabel: string | null
  summaryKey: string
  summaryFallback: string
  criticalFindings: string[]
  sections: ReportSection[]
  providerLabel: string
}

function blacklistTone(data: ImeiCheckPayload): SectionChipTone {
  if (data.is_stolen === true || data.is_blacklisted === true) return 'risk'
  if (data.is_stolen === false && !data.is_blacklisted) return 'ok'
  return 'unknown'
}

function chipFromFlag(
  flag: boolean | null | undefined,
  bad: SectionChipTone,
  good: SectionChipTone = 'ok',
): SectionChipTone {
  if (flag === true) return bad
  if (flag === false) return good
  return 'unknown'
}

export function maskImeiDigits(digits: string): string {
  if (digits.length <= 8) return digits
  return `${digits.slice(0, 6)}******${digits.slice(-3)}`
}

/** True when full digits produce the same masked form as a saved report row. */
export function imeiDigitsMatchMasked(digits: string, masked: string): boolean {
  const normalized = digits.replace(/\D/g, '')
  if (normalized.length !== 15 || !masked.trim()) return false
  return maskImeiDigits(normalized) === masked.trim()
}

export function buildImeiReportViewModel(
  data: ImeiCheckPayload,
  service: ImeiHubServiceId,
): ImeiReportViewModel {
  const device = data.device ?? undefined
  const brand = data.brand?.trim() || ''
  const model = data.model?.trim() || ''
  const deviceLabel = formatVerifyDeviceTitle(brand, model)
  const provider = device?.provider ?? 'live'
  const providerLabel = provider === 'dev-mock' ? 'DEV' : 'reDevicer'

  const criticalFindings: string[] = []
  if (data.is_stolen || data.is_blacklisted) {
    criticalFindings.push('vc.imeiReport.finding.blacklist')
  }
  if (data.is_financed) criticalFindings.push('vc.imeiReport.finding.financed')
  if (data.icloud_locked) criticalFindings.push('vc.imeiReport.finding.icloud')

  if (!data.is_valid) {
    return {
      status: 'invalid',
      trustScore: null,
      deviceLabel,
      summaryKey: 'vc.imeiReport.summary.invalid',
      summaryFallback: data.error || 'Invalid IMEI format.',
      criticalFindings,
      sections: [],
      providerLabel,
    }
  }

  if (data.imei_eu_balance_required) {
    return {
      status: 'balance',
      trustScore: null,
      deviceLabel,
      summaryKey: 'vc.imeiReport.summary.balance',
      summaryFallback: 'IMEI format OK — device data could not be loaded. Try again later.',
      criticalFindings,
      sections: [
        { id: 'imei', tone: 'ok', detail: 'vc.imeiReport.section.imeiOk' },
        { id: 'device', tone: 'unknown' },
      ],
      providerLabel,
    }
  }

  let trustScore = 100
  if (data.is_stolen || data.is_blacklisted) trustScore = 12
  else {
    if (data.icloud_locked) trustScore -= 28
    if (data.is_financed) trustScore -= 32
    if (!deviceLabel) trustScore -= 8
    trustScore = Math.max(18, Math.min(100, trustScore))
  }

  let status: ImeiReportStatus = 'clean'
  if (data.is_stolen || data.is_blacklisted) status = 'high_risk'
  else if (data.icloud_locked || data.is_financed) status = 'warning'
  else if (trustScore < 75) status = 'warning'

  const sections: ReportSection[] = [
    {
      id: 'imei',
      tone: data.is_valid ? 'ok' : 'risk',
      detail: data.is_valid ? 'vc.imeiReport.section.imeiOk' : undefined,
    },
    {
      id: 'device',
      tone: deviceLabel ? 'ok' : 'unknown',
      detail: deviceLabel
        ? [
            deviceLabel,
            device?.color,
            device?.release_year ? String(device.release_year) : null,
            device?.tac ? `TAC ${device.tac}` : null,
          ]
            .filter(Boolean)
            .join(' · ')
        : undefined,
    },
    {
      id: 'blacklist',
      tone: blacklistTone(data),
    },
    {
      id: 'carrier',
      tone: data.carrier && data.carrier !== 'Unknown' ? 'ok' : 'unknown',
      detail: data.carrier && data.carrier !== 'Unknown' ? data.carrier : undefined,
    },
    {
      id: 'lock',
      tone: chipFromFlag(data.icloud_locked, 'warn'),
    },
    {
      id: 'finance',
      tone: chipFromFlag(data.is_financed, 'warn'),
    },
    {
      id: 'mdm',
      tone: service === 'business' ? 'unknown' : 'muted',
    },
  ]

  if (service === 'android') {
    sections.push({ id: 'android', tone: 'unknown' })
  }

  let summaryKey = 'vc.imeiReport.summary.clean'
  if (status === 'high_risk') summaryKey = 'vc.imeiReport.summary.highRisk'
  else if (status === 'warning') {
    if (data.icloud_locked) summaryKey = 'vc.imeiReport.summary.icloud'
    else if (data.is_financed) summaryKey = 'vc.imeiReport.summary.financed'
    else summaryKey = 'vc.imeiReport.summary.warning'
  }

  return {
    status,
    trustScore: status === 'high_risk' ? Math.min(trustScore, 25) : trustScore,
    deviceLabel,
    summaryKey,
    summaryFallback: data.message || '',
    criticalFindings,
    sections,
    providerLabel,
  }
}
